import { Head, Link, useForm } from '@inertiajs/react';
import { useState, useEffect } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import {
    ArrowLeftIcon,
    TrashIcon,
    UserGroupIcon,
} from '@heroicons/react/24/outline';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getDayName(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return DAYS[d.getDay()] || '';
}

export default function Create({ employees, locations, flash }) {
    // Check if employee is on leave for a given date
    const isOnLeave = (emp, dateStr) => {
        if (!emp.leaves || !dateStr) return false;
        return emp.leaves.some(l => dateStr >= l.start_date && dateStr <= l.end_date);
    };

    const today = new Date().toISOString().split('T')[0];

    // Build entries for employees NOT on leave for today
    const buildEntries = (dateStr) => {
        return (employees || [])
            .filter(emp => !isOnLeave(emp, dateStr))
            .map(emp => ({
                employee_id: emp.id,
                work_location_id: emp.work_location_id || locations?.[0]?.id || '',
                ord_time: 8,
                overtime: 0,
                note: '',
                is_absent: false,
                is_sick_leave: false,
            }));
    };

    const { data, setData, post, processing, errors } = useForm({
        date: today,
        notes: '',
        is_holiday: false,
        entries: buildEntries(today),
    });

    const [dayName, setDayName] = useState(getDayName(data.date));

    // Determine if it's a rest day (Sunday or holiday)
    const isRestDay = dayName === 'Sunday' || data.is_holiday;

    const handleDateChange = (val) => {
        setData(prev => ({
            ...prev,
            date: val,
            entries: buildEntries(val),
        }));
        setDayName(getDayName(val));
    };

    // Count employees on leave for selected date
    const employeesOnLeave = (employees || []).filter(emp => isOnLeave(emp, data.date));

    // When switching to rest day, zero out all ORD times
    useEffect(() => {
        if (isRestDay) {
            setData(prev => {
                const hasNonZero = prev.entries.some(e => e.ord_time !== 0);
                if (!hasNonZero) return prev;
                return {
                    ...prev,
                    entries: prev.entries.map(e => ({ ...e, ord_time: 0 })),
                };
            });
        }
    }, [isRestDay]);

    const removeEntry = (i) => {
        setData(prev => ({
            ...prev,
            entries: prev.entries.filter((_, idx) => idx !== i),
        }));
    };

    const updateEntry = (i, field, val) => {
        setData(prev => {
            const entries = [...prev.entries];
            entries[i] = { ...entries[i], [field]: val };
            return { ...prev, entries };
        });
    };

    const submit = (e) => {
        e.preventDefault();
        post('/timesheets');
    };

    return (
        <AppLayout>
            <Head title="New TimeSheet" />
            <PageHeader
                title="New TimeSheet"
                subtitle={
                    <span className="flex items-center gap-2">
                        <Link href="/timesheets" className="text-emerald-700 hover:text-emerald-800 hover:underline">TimeSheet</Link>
                        <span className="text-slate-400">/</span>
                        <span>New TimeSheet</span>
                    </span>
                }
                actions={
                    <Button href="/timesheets" variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            <form onSubmit={submit}>
                {/* General Info */}
                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">General Information</h3>
                    </div>
                    <CardBody className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormInput
                                label="Date"
                                type="date"
                                value={data.date}
                                onChange={e => handleDateChange(e.target.value)}
                                error={errors.date}
                                required
                            />
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Day</label>
                                <div className={`w-full rounded-xl border text-sm px-4 py-2.5 ${
                                    dayName === 'Sunday'
                                        ? 'border-orange-300 bg-orange-50 text-orange-700 font-medium'
                                        : 'border-slate-200 bg-slate-50 text-slate-600'
                                }`}>
                                    {dayName || '—'}
                                    {dayName === 'Sunday' && <span className="ml-2 text-[10px] uppercase tracking-wide">Rest Day</span>}
                                </div>
                            </div>
                            <FormInput
                                label="Notes"
                                value={data.notes}
                                onChange={e => setData('notes', e.target.value)}
                                error={errors.notes}
                                placeholder="Optional notes..."
                            />
                        </div>
                        {/* Holiday Checkbox */}
                        <div className="pt-1">
                            <label className={`inline-flex items-center gap-3 cursor-pointer select-none rounded-xl border px-4 py-3 transition ${
                                data.is_holiday ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={data.is_holiday}
                                    onChange={e => setData(prev => ({ ...prev, is_holiday: e.target.checked }))}
                                    className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-slate-700">Public Holiday</span>
                                    <p className="text-[11px] text-slate-400 mt-0.5">Mark this day as a public holiday. ORD time will not apply.</p>
                                </div>
                            </label>
                        </div>
                    </CardBody>
                </Card>

                {/* Employee Entries */}
                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <div className="flex items-center gap-2 flex-wrap">
                            <UserGroupIcon className="w-5 h-5 text-slate-400" />
                            <h3 className="font-semibold text-slate-800">Employee Entries</h3>
                            <span className="text-xs text-slate-400">({data.entries.length} employees)</span>
                            {isRestDay && (
                                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-700 rounded-full">
                                    Rest Day — Overtime Only
                                </span>
                            )}
                            {employeesOnLeave.length > 0 && (
                                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 rounded-full">
                                    {employeesOnLeave.length} on leave
                                </span>
                            )}
                        </div>
                    </div>
                    <CardBody>
                        <div className="space-y-4">
                            {data.entries.map((entry, i) => {
                                const emp = employees?.find(em => em.id == entry.employee_id);
                                const rowDisabled = entry.is_absent || entry.is_sick_leave;
                                const ordDisabled = isRestDay || rowDisabled;
                                const fieldDisabled = rowDisabled;
                                return (
                                    <div key={i} className={`p-4 rounded-xl border transition ${
                                        entry.is_absent
                                            ? 'bg-red-50/50 border-red-200'
                                            : entry.is_sick_leave
                                                ? 'bg-amber-50/50 border-amber-200'
                                                : 'bg-slate-50 border-slate-200'
                                    }`}>
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-medium text-slate-700">Employee #{i + 1}</span>
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setData(prev => {
                                                        const entries = [...prev.entries];
                                                        const newVal = !entries[i].is_absent;
                                                        entries[i] = { ...entries[i], is_absent: newVal, is_sick_leave: newVal ? false : entries[i].is_sick_leave };
                                                        return { ...prev, entries };
                                                    })}
                                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setData(prev => { const entries = [...prev.entries]; const newVal = !entries[i].is_absent; entries[i] = { ...entries[i], is_absent: newVal, is_sick_leave: newVal ? false : entries[i].is_sick_leave }; return { ...prev, entries }; }); } }}
                                                    className={`inline-flex items-center gap-1.5 cursor-pointer select-none px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                                                        entry.is_absent ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                    }`}
                                                >
                                                    <span className={`inline-flex w-4 h-4 shrink-0 rounded border-2 items-center justify-center ${
                                                        entry.is_absent ? 'border-red-600 bg-red-600' : 'border-slate-300 bg-white'
                                                    }`}>
                                                        {entry.is_absent && <span className="text-white text-xs leading-none">✓</span>}
                                                    </span>
                                                    Absent
                                                </div>
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setData(prev => {
                                                        const entries = [...prev.entries];
                                                        const newVal = !entries[i].is_sick_leave;
                                                        entries[i] = { ...entries[i], is_sick_leave: newVal, is_absent: newVal ? false : entries[i].is_absent };
                                                        return { ...prev, entries };
                                                    })}
                                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setData(prev => { const entries = [...prev.entries]; const newVal = !entries[i].is_sick_leave; entries[i] = { ...entries[i], is_sick_leave: newVal, is_absent: newVal ? false : entries[i].is_absent }; return { ...prev, entries }; }); } }}
                                                    className={`inline-flex items-center gap-1.5 cursor-pointer select-none px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                                                        entry.is_sick_leave ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                    }`}
                                                >
                                                    <span className={`inline-flex w-4 h-4 shrink-0 rounded border-2 items-center justify-center ${
                                                        entry.is_sick_leave ? 'border-amber-600 bg-amber-600' : 'border-slate-300 bg-white'
                                                    }`}>
                                                        {entry.is_sick_leave && <span className="text-white text-xs leading-none">✓</span>}
                                                    </span>
                                                    Sick Leave
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => removeEntry(i)} className="p-1.5 hover:bg-red-50 rounded-lg">
                                                <TrashIcon className="w-4 h-4 text-red-400" />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Employee</label>
                                                <div className={`w-full rounded-xl border text-sm px-4 py-2.5 ${
                                                    rowDisabled
                                                        ? 'border-red-200 bg-red-50 text-slate-400 line-through'
                                                        : 'border-slate-200 bg-white text-slate-800'
                                                }`}>
                                                    {emp ? `${emp.employee_code} — ${emp.full_name}` : '—'}
                                                </div>
                                                {emp && (emp.work_location_name || emp.department_name) && (
                                                    <p className="mt-1 text-[11px] text-slate-400 leading-tight">
                                                        {emp.work_location_name && <span>{emp.work_location_name}</span>}
                                                        {emp.work_location_name && emp.department_name && <span> / </span>}
                                                        {emp.department_name && <span>{emp.department_name}</span>}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                                    ORD Time {!ordDisabled && <span className="text-red-500">*</span>}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.5"
                                                    value={ordDisabled ? 0 : entry.ord_time}
                                                    onChange={e => updateEntry(i, 'ord_time', e.target.value)}
                                                    disabled={ordDisabled}
                                                    className={`w-full rounded-xl border text-sm px-4 py-2.5 transition ${
                                                        ordDisabled
                                                            ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                            : 'border-slate-200 text-slate-800 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20'
                                                    }`}
                                                />
                                                {errors[`entries.${i}.ord_time`] && <p className="mt-1 text-xs text-red-500">{errors[`entries.${i}.ord_time`]}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Overtime</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.5"
                                                    value={fieldDisabled ? 0 : entry.overtime}
                                                    onChange={e => updateEntry(i, 'overtime', e.target.value)}
                                                    disabled={fieldDisabled}
                                                    className={`w-full rounded-xl border text-sm px-4 py-2.5 transition ${
                                                        fieldDisabled
                                                            ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                            : 'border-slate-200 text-slate-800 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20'
                                                    }`}
                                                />
                                                {errors[`entries.${i}.overtime`] && <p className="mt-1 text-xs text-red-500">{errors[`entries.${i}.overtime`]}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
                                                <input
                                                    type="text"
                                                    value={entry.note}
                                                    onChange={e => updateEntry(i, 'note', e.target.value)}
                                                    placeholder={fieldDisabled ? 'Absent' : 'Optional note...'}
                                                    disabled={fieldDisabled}
                                                    className={`w-full rounded-xl border text-sm px-4 py-2.5 transition ${
                                                        fieldDisabled
                                                            ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                            : 'border-slate-200 text-slate-800 placeholder-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20'
                                                    }`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {errors.entries && <p className="mt-2 text-xs text-red-500">{errors.entries}</p>}
                    </CardBody>
                </Card>

                <div className="flex gap-3">
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Creating...' : 'Create TimeSheet'}
                    </Button>
                    <Button href="/timesheets" variant="outline">Cancel</Button>
                </div>
            </form>
        </AppLayout>
    );
}
